/* Estimated Shipping Date logic
 * - Finds .esd containers and renders cutoff countdown and delivery date
 * - Cutoff hour is in store's local time (configured by block)
 * - Closed dates (bank holidays, shutdowns) are skipped like non-working days
 */
(function () {
  // Upper bound on the day-by-day scan, so a misconfigured working-day or
  // closed-date list can never spin forever inside the 1s tick.
  const MAX_DAY_SCAN = 400;

  function pad(num) {
    return String(num).padStart(2, '0');
  }

  function parseWorkingDays(csv) {
    return (csv || '1,2,3,4,5')
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !Number.isNaN(n));
  }

  function dateKey(y, m, d) {
    return y + '-' + m + '-' + d;
  }

  /** Parses a comma separated list of YYYY-MM-DD closed dates into a lookup set. */
  function parseClosedDates(csv) {
    const closed = new Set();
    (csv || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((iso) => {
        const match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(iso);
        if (match) closed.add(dateKey(+match[1], +match[2], +match[3]));
      });
    return closed;
  }

  function isWorkingDay(y, m, d, wd, working, closed) {
    return working.indexOf(wd) > -1 && !closed.has(dateKey(y, m, d));
  }

  // Timezone helpers (Europe/London by default)
  function getPartsInTz(d, tz) {
    const fmt = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: false,
      weekday: 'short',
    });
    const parts = fmt.formatToParts(d);
    const map = {};
    parts.forEach((p) => (map[p.type] = p.value));
    const weekdayIndex = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(map.weekday);
    return {
      y: +map.year,
      m: +map.month,
      d: +map.day,
      h: +map.hour,
      i: +map.minute,
      s: +map.second,
      wd: weekdayIndex,
    };
  }
  function nextDateUTC(y, m, d) {
    const u = new Date(Date.UTC(y, m - 1, d));
    u.setUTCDate(u.getUTCDate() + 1);
    return { y: u.getUTCFullYear(), m: u.getUTCMonth() + 1, d: u.getUTCDate() };
  }
  function advanceToNextWorking(y, m, d, wd, working, closed) {
    let yy = y,
      mm = m,
      dd = d,
      w = wd,
      scanned = 0;
    while (!isWorkingDay(yy, mm, dd, w, working, closed) && scanned < MAX_DAY_SCAN) {
      const nd = nextDateUTC(yy, mm, dd);
      yy = nd.y;
      mm = nd.m;
      dd = nd.d;
      w = (w + 1) % 7;
      scanned++;
    }
    return { y: yy, m: mm, d: dd, wd: w };
  }
  function addWorkingDaysFrom(y, m, d, wd, n, working, closed) {
    let added = 0,
      yy = y,
      mm = m,
      dd = d,
      w = wd,
      scanned = 0;
    while (added < n && scanned < MAX_DAY_SCAN) {
      const nd = nextDateUTC(yy, mm, dd);
      yy = nd.y;
      mm = nd.m;
      dd = nd.d;
      w = (w + 1) % 7;
      scanned++;
      if (isWorkingDay(yy, mm, dd, w, working, closed)) added++;
    }
    return { y: yy, m: mm, d: dd, wd: w };
  }
  function monthName(y, m, d, tz, locale) {
    try {
      return new Intl.DateTimeFormat(locale || 'en-GB', { timeZone: tz, month: 'long' }).format(
        new Date(Date.UTC(y, m - 1, d))
      );
    } catch (e) {
      return new Date(Date.UTC(y, m - 1, d)).toUTCString().split(' ')[2];
    }
  }

  function update(container) {
    const cutoffHour = parseInt(container.getAttribute('data-cutoff-hour') || '14', 10);
    const workingDays = parseWorkingDays(container.getAttribute('data-working-days'));
    const closedDates = parseClosedDates(container.getAttribute('data-closed-dates'));
    const tMin = parseInt(container.getAttribute('data-transit-min') || '1', 10);
    const tMax = parseInt(container.getAttribute('data-transit-max') || '1', 10);
    const locale = container.getAttribute('data-locale') || 'en-GB';
    const tz = container.getAttribute('data-tz') || 'Europe/London';

    const now = new Date();
    const parts = getPartsInTz(now, tz);
    const secsNow = parts.h * 3600 + parts.i * 60 + parts.s;
    const secsCut = cutoffHour * 3600;
    let secsDiff = secsCut - secsNow;
    if (secsDiff <= 0) secsDiff += 24 * 3600;
    const h = pad(Math.floor(secsDiff / 3600));
    const m = pad(Math.floor((secsDiff % 3600) / 60));
    const s = pad(secsDiff % 60);
    const countdownEl = container.querySelector('.js-esd-countdown');
    if (countdownEl) countdownEl.textContent = h + ':' + m + ':' + s;

    // Base ship date in UK tz
    let base = { y: parts.y, m: parts.m, d: parts.d, wd: parts.wd };
    if (secsNow >= secsCut) {
      const nd = nextDateUTC(base.y, base.m, base.d);
      base = { y: nd.y, m: nd.m, d: nd.d, wd: (base.wd + 1) % 7 };
    }
    base = advanceToNextWorking(base.y, base.m, base.d, base.wd, workingDays, closedDates);

    const min = addWorkingDaysFrom(base.y, base.m, base.d, base.wd, tMin, workingDays, closedDates);
    const max =
      tMax > tMin
        ? addWorkingDaysFrom(base.y, base.m, base.d, base.wd, tMax, workingDays, closedDates)
        : null;
    const dayStr = (d) => d.d + '. ' + monthName(d.y, d.m, d.d, tz, locale);
    const dateText = max ? dayStr(min) + ' – ' + dayStr(max) : dayStr(min);
    const dateEl = container.querySelector('.js-esd-date');
    if (dateEl) dateEl.textContent = dateText;
  }

  function init() {
    const containers = document.querySelectorAll('.esd');
    if (!containers.length) return;
    containers.forEach(update);
    // Tick countdowns
    setInterval(() => containers.forEach(update), 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
